import { ReactNode, useEffect, useReducer, useState } from 'react';
import { getGetTherapist, validateAdmin } from '../utils/firebase';
import { printLogs } from '../utils/logs';
import firebase from 'firebase/app';

import { AuthContext, EThreeType } from './FirebaseContext';
// @types
import { ActionMap, AuthState, AuthUser, Roles } from '../@types/authentication';
import { getVirgilJwt } from '../utils/firebase/cloudFunctions';

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  roles: {}
};

enum Types {
  Initial = 'INITIALISE'
}

type FirebaseAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean;
    user: AuthUser;
    roles?: Roles;
    eThree?: EThreeType;
    therapistId?: string;
  };
};
type FirebaseActions = ActionMap<FirebaseAuthPayload>[keyof ActionMap<FirebaseAuthPayload>];

const reducer = (state: AuthState, action: FirebaseActions) => {
  if (action.type === 'INITIALISE') {
    const { roles, therapistId, isAuthenticated, user, eThree } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      roles,
      therapistId,
      eThree
    };
  }

  return state;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<firebase.firestore.DocumentData | undefined>();
  const [state, dispatch] = useReducer(reducer, initialState);
  //@ts-ignore
  const [eThree, setEThree] = useState<EThreeType>();
  useEffect(
    () =>
      firebase.auth().onAuthStateChanged(async (user) => {
        try {
          if (user?.uid) {
            const { data } = await validateAdmin(user?.uid);
            const therapist = await getGetTherapist(user.uid)
              .get()
              .then((snap) => snap?.docs?.[0]?.data());

            if (data?.admin || data?.chat) {
              setProfile(user);

              const initializeFunction = () =>
                getVirgilJwt({ identity: user.uid }).then((result) => {
                  return result.data.token;
                });
              //@ts-ignore
              await window.E3kit.EThree.initialize(initializeFunction)
                .then(async (e3: EThreeType) => {
                  if (e3) {
                    setEThree(e3);
                    dispatch({
                      type: Types.Initial,
                      payload: {
                        isAuthenticated: true,
                        user,
                        roles: data,
                        therapistId: therapist?.id,
                        eThree: e3
                      }
                    });
                    const hasLocalPrivateKey: boolean = await e3.hasLocalPrivateKey();
                    printLogs({ hasLocalPrivateKey });
                    if (!hasLocalPrivateKey) {
                      const res1 = await e3.restorePrivateKey(user.uid);
                      printLogs({ res1, isRestoredPrivateKey: true });
                    }
                  }
                })
                .catch((error: any) => console.log(error, 'init error'));
            } else {
              throw Error('No access');
            }
          } else {
            throw Error('unable to authorize.');
          }
        } catch (e: any) {
          console.log(e);
          setProfile(undefined);
          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: false, user: null }
          });
        }
      }), // eslint-disable-next-line
    [dispatch]
  );

  const login = (email: string, password: string) =>
    firebase.auth().signInWithEmailAndPassword(email, password);

  const loginWithGoogle = () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const loginWithFaceBook = () => {
    const provider = new firebase.auth.FacebookAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const loginWithTwitter = () => {
    const provider = new firebase.auth.TwitterAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const register = (email: string, password: string, firstName: string, lastName: string) =>
    firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then((res) => {
        firebase
          .firestore()
          .collection('users')
          .doc(res.user?.uid)
          .set({
            uid: res.user?.uid,
            email,
            displayName: `${firstName} ${lastName}`
          });
      });

  const logout = async () => {
    await eThree?.cleanup();
    await firebase.auth().signOut();
  };

  const resetPassword = async (email: string) => {
    await firebase.auth().sendPasswordResetEmail(email);
  };

  const updateProfile = async () => {
    // await firebase.auth().updateCurrentUser(user);
    // return true;
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'firebase',
        user: { ...(profile || {}) },
        // @ts-ignore
        roles: state?.roles,
        login,
        register,
        loginWithGoogle,
        loginWithFaceBook,
        loginWithTwitter,
        logout,
        resetPassword,
        updateProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
